import {
  isBaseline,
  isCore,
  isCorePaid,
  isCoreWinback,
  isEn,
  isEnUS,
  isEnIntl,
  isInApp,
  isInApp14DayiOS,
  isWinback,
} from ".";
import { deviceOS, routeConstants } from "../constants";
import store, { CoreReduxState } from "../redux/store";

import { isFromEmail, isEmailWinback, isEnUSEmail } from "./email";
import { isUS } from "./locale";
import { checkSegments } from "./util";

export function isDevMode(state = store.getState()) {
  return !!state.serverContext.idm;
}

export const isCollapsedPaymentEligible = (state?: CoreReduxState) => {
  return isEn(state) && !isInApp(state);
};

export const isHMEligible = (state = store.getState()) =>
  isEnUS(state) &&
  isCore(state) &&
  state.deviceInformation.device_os === deviceOS.iOS;

/**
 * Verifies that the user is eligible for Compass styles.
 * Currently only baselined for all English buyflows
 */
export const isCompassEligible = isEn;

/**
 * Checks if the user can use the native app in-app purchase flow.
 */
export const isInAppNativePurchaseEligible = checkSegments({
  required: isEnUS,
  oneOf: [isInApp14DayiOS],
});

/**
 * Checks if the user can use the web-based in-app purchase flow.
 */
export const isInAppWebPurchaseEligible = checkSegments({
  oneOf: [
    routeConstants.app14DayFtWeb,
    routeConstants.app14DayWeb,
    routeConstants.app14DayWebReturning,
  ],
});

export const isFastForwardEligible = checkSegments({
  required: isEnUS,
  oneOf: [isCore, routeConstants.winback],
});

const isNoomPremiumEligibleUS = checkSegments({
  required: isEnUS,
  oneOf: [
    isBaseline,
    isCoreWinback,
    routeConstants.affiliateTrialNPB,
    routeConstants.emailMain,
    routeConstants.referrals,
  ],
});

const isNoomPremiumEligibleIntl = checkSegments({
  required: isEnIntl,
  oneOf: [
    isBaseline,
    isCoreWinback,
    routeConstants.affiliateTrialNPB,
    routeConstants.emailMain,
    routeConstants.referrals,
    routeConstants.freeTrial,
  ],
});

export const isNoomPremiumEligible = () => {
  return isNoomPremiumEligibleUS() || isNoomPremiumEligibleIntl();
};

export const isWelcomeToNoomEligible = checkSegments({
  oneOf: [
    isBaseline,
    routeConstants.winback,
    routeConstants.app14DayWeb,
    routeConstants.app14Day,
  ],
});

export const isConsultationEligible = checkSegments({
  required: isUS,
  oneOf: [
    isBaseline,
    routeConstants.affiliateTrialNPB,
    routeConstants.referrals,
  ],
});

export const isImportantDateEligible = checkSegments({
  required: isEnUS,
  oneOf: [isBaseline, isCoreWinback, isFromEmail],
});

/**
 * Implements the logic for targeting users for dynamic plan graph(ex417)
 */
export const isDynamicPlanGraphEligible = checkSegments({
  oneOf: [isBaseline, isWinback, isEnUSEmail, isInApp],
});

export const isBuildPlanEligible = (state = store.getState()) => {
  return (
    isInAppWebPurchaseEligible(state) ||
    isBaseline(state) ||
    isWinback(state) ||
    (isEnUS(state) && isEmailWinback(state))
  );
};

export const isTrialEligible = ({ serverContext } = store.getState()) => {
  return serverContext.plan.has_trial && !serverContext.has_no_trial;
};

export const isPromoCodeBoxDisabled = checkSegments({
  required: isEn,
  oneOf: [isInApp, isCorePaid, isWinback, routeConstants.affiliateNoTrial],
});

export const shouldValidateTokenFlag = checkSegments({
  oneOf: [routeConstants.appUpsell],
});

export const shouldShowOnscreenBackButtons = checkSegments({
  oneOf: [
    routeConstants.app14DayWebReturning,
    routeConstants.app14DayWeb,
    routeConstants.app14DayFtWeb,
    routeConstants.app14Day,
    routeConstants.appNoTrial,
  ],
});

export const shouldShowExplainerLoadingPage = checkSegments({
  oneOf: [
    isBaseline,
    isWinback,
    routeConstants.app14Day,
    routeConstants.app14DayWeb,
  ],
});

export const shouldUseMobileOptimizedControls = checkSegments({
  oneOf: [
    isBaseline,
    isWinback,
    routeConstants.app14Day,
    routeConstants.app14DayWeb,
    routeConstants.hmPaid,
    routeConstants.hmDirect,
  ],
});
